/**
 * TimeField — a time you can type ("6:30p", "18:30", "630pm") or pick from a
 * 15-minute list that narrows as you type. Arrow keys move through the list
 * and Enter picks. The value is 24-hour `HH:MM`, the same as the browser time
 * input it replaces, so the form behind it does not change.
 */
import { useId, useMemo, useState } from 'react';
import type { KeyboardEvent } from 'react';

import { formatTimeLabel, parseTimeInput, timeOptions } from './date-time-input';
import { fieldStyle } from './field-style';
import { TimeOptionList } from './TimeOptionList';
import { usePickerField } from './use-picker-field';

export interface TimeFieldProps {
  value: string;
  onChange: (value: string) => void;
  /** The field's accessible name. */
  ariaLabel: string;
  error?: boolean;
  placeholder?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
}

const ALL_TIMES = timeOptions(15);

const squash = (text: string) => text.toLowerCase().replace(/[\s.]/g, '');

/** The times that still match what has been typed; all of them before any
 *  typing. Shorthand that already reads as a time ("6p", "630pm") matches no
 *  label by its letters, so it keeps the whole list, which opens scrolled to
 *  the time it applied. */
function matchingTimes(typed: string | null): string[] {
  const q = typed === null ? '' : squash(typed);
  if (q === '') return ALL_TIMES;
  const matches = ALL_TIMES.filter(
    (t) => squash(formatTimeLabel(t)).startsWith(q) || t.startsWith(q),
  );
  return matches.length === 0 && parseTimeInput(typed ?? '') ? ALL_TIMES : matches;
}

/** Arrow keys step the highlight; Enter picks it. Returns true when handled. */
function stepHighlight(
  e: KeyboardEvent<HTMLInputElement>,
  options: string[],
  active: string | null,
  setActive: (t: string | null) => void,
): boolean {
  if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return false;
  e.preventDefault();
  const at = active === null ? -1 : options.indexOf(active);
  const next = e.key === 'ArrowDown' ? Math.min(at + 1, options.length - 1) : Math.max(at - 1, 0);
  setActive(options[next] ?? null);
  return true;
}

export function TimeField({
  value,
  onChange,
  ariaLabel,
  error,
  placeholder = 'e.g. 6:30 PM',
  ...aria
}: TimeFieldProps) {
  const listId = useId();
  const [active, setActive] = useState<string | null>(null);
  const field = usePickerField({ value, onChange, parse: parseTimeInput, label: formatTimeLabel });
  const options = useMemo(() => matchingTimes(field.typed), [field.typed]);

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (field.open && stepHighlight(e, options, active, setActive)) return;
    if (e.key === 'Enter' && field.open && active) {
      e.preventDefault();
      field.choose(active);
      setActive(null);
      return;
    }
    field.onKeyDown(e);
  };

  return (
    <div ref={field.wrapperRef} onBlur={field.onBlur} style={{ position: 'relative' }}>
      <input
        type="text"
        role="combobox"
        aria-label={ariaLabel}
        aria-controls={listId}
        aria-expanded={field.open}
        aria-activedescendant={active ? `${listId}-${active}` : undefined}
        autoComplete="off"
        placeholder={placeholder}
        value={field.display}
        onFocus={() => field.setOpen(true)}
        onClick={() => field.setOpen(true)}
        onChange={(e) => {
          setActive(null);
          field.onInputChange(e.target.value);
        }}
        onKeyDown={onKeyDown}
        style={fieldStyle(error ? 'error' : undefined)}
        {...aria}
      />
      {field.open && options.length > 0 && (
        <TimeOptionList
          id={listId}
          options={options}
          value={value}
          active={active}
          onPick={field.choose}
        />
      )}
    </div>
  );
}
